import type { Match, Query } from '@atlassian/react-resource-router';

/**
 * The backend only parses the path and query of `currentURL`, so the origin is a
 * placeholder rather than the real host.
 */
const PLACEHOLDER_ORIGIN = 'http://example.com';

/**
 * Query parameters that can identify the project the user is currently on.
 *
 * The order is fixed so the same location always serialises to the same string —
 * `URLSearchParams` would otherwise preserve the (arbitrary) order the router
 * happened to parse them in.
 */
const PROJECT_QUERY_PARAMS = [
	'projectKey',
	'project.key',
	'projectId',
	'project.id',
	'pid',
	'selectedProjectId',
	'selectedIssue',
] as const;

/**
 * Project keys cannot contain a hyphen, so the portion of an issue key before the
 * first hyphen is the key of the project that issue belongs to.
 */
const toProjectKey = (issueKey: string): string => issueKey.split('-')[0];

/**
 * A segment identifies the project when it is the project key itself
 * (`.../projects/TEST/...`) or an issue in that project (`/browse/TEST-123`).
 */
const identifiesProject = (segment: string, projectKey: string): boolean => {
	const normalisedSegment = segment.toLowerCase();
	const normalisedProjectKey = projectKey.toLowerCase();

	return (
		normalisedSegment === normalisedProjectKey ||
		normalisedSegment.startsWith(`${normalisedProjectKey}-`)
	);
};

/**
 * Reduces the path to the project it belongs to, so every page within a project
 * collapses to the same URL.
 *
 * The last matching segment is used rather than the first, because a project key
 * can also be a literal segment earlier in the path — project `C` on
 * `/jira/software/c/projects/C/boards/1` must reduce to `/jira/software/c/projects/C`
 * and not to `/jira/software/c`.
 */
const truncateAtProjectKey = (path: string, projectKey: string | undefined): string | undefined => {
	if (!projectKey) {
		return undefined;
	}

	const segments = path.split('/');

	for (let index = segments.length - 1; index >= 0; index--) {
		if (identifiesProject(segments[index], projectKey)) {
			// An issue key segment is reduced to its project so that moving between
			// issues in the same project does not change the URL.
			return [...segments.slice(0, index), toProjectKey(segments[index])].join('/');
		}
	}

	return undefined;
};

const getProjectSearch = (query: Query | undefined): string => {
	const searchParams = new URLSearchParams();

	PROJECT_QUERY_PARAMS.forEach((param) => {
		const value = query?.[param];
		if (value) {
			searchParams.append(param, value);
		}
	});

	const search = searchParams.toString();
	return search ? `?${search}` : '';
};

/**
 * Builds the `currentURL` variable sent to `jira_projectsSidebarMenu`, reduced to
 * the parts of the location that identify the current project.
 *
 * The backend reads this URL for one reason: to work out which project the user is
 * on, so that project can be shown in the sidebar even when it is not already one
 * of their recents. The raw location is a poor carrier for that intent, because it
 * is also a Relay variable and therefore part of the sidebar query's cache key. Any
 * change refetches the query and unmounts the sidebar, and the raw location changes
 * constantly — on-load redirects walk deeper into a project (`.../boards` becomes
 * `.../boards/123`) and unrelated query parameters are rewritten as the page
 * settles. That produced continuous sidebar flashing.
 *
 * Reducing the location to the project keeps the variable stable across those
 * changes, so the query is only refetched when the answer could actually differ.
 * The result is always a URL shape Jira itself serves — a project page such as
 * `/jira/software/projects/TEST`, or `/browse/TEST` for issue routes — so the
 * backend resolves it the same way it resolves the raw location.
 *
 * Returns `undefined` when the location identifies no project, which keeps every
 * such route on a single value rather than one per page.
 */
export const getProjectsSidebarCurrentUrl = (
	match: Match | undefined,
	query: Query | undefined,
): string | undefined => {
	const path = match?.url;
	if (!path) {
		// No route to describe — callers that load this from the client rather than
		// from the router have no location to send.
		return undefined;
	}

	const { issueKey, projectKey } = match.params ?? {};
	const projectPath = truncateAtProjectKey(
		path,
		projectKey || (issueKey ? toProjectKey(issueKey) : undefined),
	);

	// Once the path pins the project down, the query string can only destabilise the
	// result — parameters such as `selectedIssue` change as the user moves around
	// within the project without changing the answer.
	if (projectPath !== undefined) {
		return `${PLACEHOLDER_ORIGIN}${projectPath}`;
	}

	// Otherwise the project, if there is one, is identifiable only from the query —
	// the issue navigator being the common case.
	const projectSearch = getProjectSearch(query);
	if (projectSearch) {
		return `${PLACEHOLDER_ORIGIN}${path}${projectSearch}`;
	}

	// There is no current project on this route, so there is nothing for the backend
	// to surface. Returning the bare location instead would make every dashboard,
	// filter and settings page a distinct value, refetching the menu — and blanking
	// it — on navigations that cannot change the answer.
	return undefined;
};
